import { useQuery } from '@tanstack/react-query';
import {
  DEFAULT_BRANDING_VALUES,
  DEFAULT_COMPANY_PROFILE,
  DEFAULT_TENANT_SETTINGS,
  type Branding,
  type CompanyProfile,
  type TenantSettings,
} from '@goyone/shared';
import { api } from './api';

export interface TenantProfile {
  tenant: { name: string; currency: string };
  branding: Branding;
  company: CompanyProfile;
  settings: TenantSettings;
}

/**
 * إعدادات المستأجر، مصدرًا واحدًا لكل الشاشات.
 *
 * نسبة الضريبة ومهلة السداد والشروط الافتراضية كانت ثوابت في الكود، فكان
 * كل مستند جديد يبدأ بنسبة أربعة عشر بالمائة وشروط فارغة مهما ضبط المستأجر
 * إعداداته. الإعداد الذي لا يُقرأ ليس إعدادًا.
 *
 * البيانات نادرة التغير، فتُخزَّن مؤقتًا خمس دقائق: تحميلها مع كل نموذج
 * يضيف طلبًا لكل فتح شاشة دون فائدة.
 */
export function useTenantProfile() {
  return useQuery({
    queryKey: ['settings'],
    queryFn: () => api<TenantProfile>('/settings'),
    staleTime: 5 * 60_000,
  });
}

/**
 * القيم الافتراضية للمستندات الجديدة.
 *
 * تُعيد قيم الحزمة المشتركة ريثما تصل الإعدادات، فلا يبدأ النموذج بحقول
 * فارغة ثم تتغير تحت يد المستخدم وهو يكتب.
 */
export function useDocumentDefaults(): {
  settings: TenantSettings;
  branding: Branding;
  company: CompanyProfile;
  ready: boolean;
} {
  const profile = useTenantProfile();
  return {
    settings: profile.data?.settings ?? DEFAULT_TENANT_SETTINGS,
    branding: profile.data?.branding ?? DEFAULT_BRANDING_VALUES,
    company: profile.data?.company ?? DEFAULT_COMPANY_PROFILE,
    ready: Boolean(profile.data),
  };
}
